import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CompanyOverview from './components/CompanyOverview';
import ProductsServicesSection from './components/ProductsServicesSection';
import LegalDocumentsSection from './components/LegalDocumentsSection';
import ContactsDirectory from './components/ContactsDirectory';
import PartnerHealthDashboard from './components/PartnerHealthDashboard';
import SetupTab from './components/SetupTab';
import NotesTab from './components/NotesTab';
import GovernanceTab from './components/GovernanceTab';
import { partnerService } from '../../services/partnerService';
import { useAuth } from '../../contexts/AuthContext';
import { exportToCSV, exportToPDF } from '../../utils/exportUtils';

// ─── Edit Profile Modal ───────────────────────────────────────────────────────
const EditProfileModal = ({ partner, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    legal_name: partner?.companyName || '',
    brand_name: partner?.brandName || '',
    tier: partner?.tier || 'Silver',
    country: partner?.country || '',
    website: partner?.website || '',
    partner_type: partner?.partnerType || 'Reseller',
    status: partner?.rawStatus || 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.legal_name?.trim()) { setError('Company name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      await partnerService?.update(partner?.id, {
        legal_name: form?.legal_name,
        brand_name: form?.brand_name,
        tier: form?.tier,
        country: form?.country,
        website: form?.website,
        partner_type: form?.partner_type,
        status: form?.status,
      });
      onSuccess();
    } catch (err) {
      setError(err?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-primary/5 to-secondary/5 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
              <Icon name="Edit" size={18} className="text-white" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Edit Partner Profile</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-28 space-y-16 overflow-y-auto flex-1">
          {error && (
            <div className="flex items-center gap-10 p-14 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-6">Company (Legal) Name *</label>
            <input name="legal_name" value={form?.legal_name} onChange={handleChange} placeholder="e.g. Acme Corporation Ltd." className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" required />
          </div>
          <div className="grid grid-cols-2 gap-16">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Brand Name</label>
              <input name="brand_name" value={form?.brand_name} onChange={handleChange} placeholder="e.g. Acme" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Country</label>
              <input name="country" value={form?.country} onChange={handleChange} placeholder="e.g. United States" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Partner Type</label>
              <select name="partner_type" value={form?.partner_type} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['Reseller', 'Distributor', 'System Integrator', 'ISV', 'MSP', 'Referral']?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-6">Tier</label>
              <select name="tier" value={form?.tier} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['Strategic', 'Gold', 'Silver', 'Tactical']?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-6">Website</label>
              <input name="website" value={form?.website} onChange={handleChange} placeholder="https://example.com" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-6">Status</label>
              <select name="status" value={form?.status} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white">
                {['draft', 'submitted', 'under_review', 'approved', 'suspended', 'inactive']?.map(s => <option key={s} value={s}>{s?.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-primary to-secondary text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={16} /> Save Changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Partner Switcher ─────────────────────────────────────────────────────────
const PartnerSwitcher = ({ currentPartnerId, onSelect }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const data = await partnerService?.getAll();
      setPartners(data || []);
    } catch (err) {
      console.error('Error loading partners:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = partners?.filter(p =>
    p?.legal_name?.toLowerCase()?.includes(search?.toLowerCase()) ||
    p?.brand_name?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const current = partners?.find(p => p?.id === currentPartnerId);

  const getTierColor = (tier) => {
    const colors = { 'Gold': 'bg-amber-100 text-amber-700', 'Silver': 'bg-slate-100 text-slate-600', 'Strategic': 'bg-purple-100 text-purple-700', 'Tactical': 'bg-blue-100 text-blue-700' };
    return colors?.[tier] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="relative z-50">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-12 px-20 py-12 bg-white border border-slate-200 rounded-xl hover:border-primary/40 hover:shadow-md transition-all"
      >
        <Icon name="Building2" size={18} className="text-slate-500" />
        <span className="text-sm font-semibold text-slate-800 max-w-[200px] truncate">
          {current?.legal_name || 'Select Partner'}
        </span>
        {current?.tier && (
          <span className={`px-8 py-2 rounded-full text-xs font-semibold ${getTierColor(current?.tier)}`}>
            {current?.tier}
          </span>
        )}
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-slate-400 ml-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-8 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 z-[70] overflow-hidden">
            <div className="p-16 border-b border-slate-100">
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-12 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search partners..."
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value)}
                  className="w-full pl-36 pr-12 py-10 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-24">
                  <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                </div>
              ) : filtered?.length > 0 ? (
                filtered?.map(p => (
                  <button
                    key={p?.id}
                    onClick={() => { onSelect(p?.id); setOpen(false); setSearch(''); }}
                    className={`w-full flex items-center gap-12 px-16 py-12 hover:bg-slate-50 transition-smooth text-left ${p?.id === currentPartnerId ? 'bg-primary/5' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {p?.legal_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{p?.legal_name}</div>
                      {p?.brand_name && <div className="text-xs text-slate-500 truncate">{p?.brand_name}</div>}
                    </div>
                    <span className={`px-8 py-2 rounded-full text-xs font-semibold flex-shrink-0 ${getTierColor(p?.tier)}`}>
                      {p?.tier || 'N/A'}
                    </span>
                    {p?.id === currentPartnerId && <Icon name="Check" size={14} className="text-primary flex-shrink-0" />}
                  </button>
                ))
              ) : (
                <div className="text-center py-24 text-sm text-slate-500">No partners found</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PartnerProfilePortal = () => {
  const navigate = useNavigate();
  const { partnerId: urlPartnerId } = useParams();
  const { userProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('overview');
  const [partnerData, setPartnerData] = useState(null);
  const [rawPartner, setRawPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPartnerId, setCurrentPartnerId] = useState(urlPartnerId || null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  useEffect(() => {
    if (urlPartnerId) setCurrentPartnerId(urlPartnerId);
  }, [urlPartnerId]);

  useEffect(() => {
    loadPartner();
  }, [currentPartnerId]);

  const loadPartner = async () => {
    setLoading(true);
    setError(null);
    try {
      let p;
      if (currentPartnerId) {
        p = await partnerService?.getById(currentPartnerId);
      } else {
        const partners = await partnerService?.getAll();
        p = partners?.[0];
        if (p) setCurrentPartnerId(p?.id);
      }
      if (p) {
        setRawPartner(p);
        setPartnerData({
          id: p?.id,
          companyName: p?.legal_name,
          brandName: p?.brand_name,
          country: p?.country,
          website: p?.website,
          partnerType: p?.partner_type,
          rawStatus: p?.status,
          logo: p?.logo_url || "https://img.rocket.new/generatedImages/rocket_gen_img_1b1ecfdd2-1768043125127.png",
          tier: p?.tier || 'Silver',
          partnershipDuration: p?.partnership_start_date
            ? `${Math.max(0, Math.floor((new Date() - new Date(p.partnership_start_date)) / (1000 * 60 * 60 * 24 * 365)))} years`
            : 'N/A',
          revenueContribution: p?.revenue_contribution
            ? `OMR ${(p?.revenue_contribution / 1000000)?.toFixed(1)}M`
            : 'OMR 0',
          healthScore: p?.health_score || 0,
          status: p?.status === 'approved' ? 'Active' : p?.status
        });
      } else {
        setPartnerData(null);
      }
    } catch (err) {
      console.error('Error loading partner:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerSwitch = (newPartnerId) => {
    setCurrentPartnerId(newPartnerId);
    setActiveSection('overview');
    navigate(`/partner-profile-portal/${newPartnerId}`, { replace: true });
  };

  const handleGenerateSummary = (format) => {
    setShowExportMenu(false);
    if (!partnerData) return;

    const rows = [{
      name: partnerData?.companyName,
      brand: partnerData?.brandName || '—',
      tier: partnerData?.tier,
      type: partnerData?.partnerType || '—',
      country: partnerData?.country || '—',
      status: partnerData?.status,
      health_score: partnerData?.healthScore,
      revenue: partnerData?.revenueContribution,
      partnership_duration: partnerData?.partnershipDuration,
      website: partnerData?.website || '—'
    }];

    const columns = ['name', 'brand', 'tier', 'type', 'country', 'status', 'health_score', 'revenue', 'partnership_duration', 'website'];
    const headers = {
      name: 'Company Name', brand: 'Brand', tier: 'Tier', type: 'Partner Type',
      country: 'Country', status: 'Status', health_score: 'Health Score',
      revenue: 'Revenue Contribution', partnership_duration: 'Partnership Duration', website: 'Website'
    };
    const filename = `${partnerData?.companyName?.replace(/\s+/g, '_')}_profile`;

    if (format === 'csv') {
      exportToCSV(rows, columns, headers, `${filename}.csv`);
    } else {
      exportToPDF({
        title: `Partner Profile: ${partnerData?.companyName}`,
        subtitle: `${partnerData?.tier} Partner — ${partnerData?.status}`,
        rows,
        columns,
        headers,
        summaryCards: [
          { label: 'Tier', value: partnerData?.tier },
          { label: 'Health Score', value: `${partnerData?.healthScore}/100` },
          { label: 'Revenue', value: partnerData?.revenueContribution },
          { label: 'Status', value: partnerData?.status }
        ]
      });
    }
  };

  const sections = [
    { id: 'overview', label: 'Company Overview', icon: 'Building2' },
    { id: 'products', label: 'Products & Services', icon: 'Package' },
    { id: 'documents', label: 'Legal Documents', icon: 'FileText' },
    { id: 'contacts', label: 'Contacts Directory', icon: 'Users' },
    { id: 'governance', label: 'Governance', icon: 'Shield' },
    { id: 'setup', label: 'Setup', icon: 'Settings' },
    { id: 'notes', label: 'Internal Notes', icon: 'StickyNote' }
  ];

  const getTierColor = (tier) => {
    const colors = { 'Gold': 'from-yellow-500 to-amber-600', 'Silver': 'from-slate-400 to-slate-500', 'Strategic': 'from-purple-500 to-indigo-600', 'Tactical': 'from-blue-500 to-cyan-600' };
    return colors?.[tier] || 'from-slate-400 to-slate-500';
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
        <Header />
        <main className="pt-[148px] flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-16">
            <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
            <p className="text-slate-500 text-sm">Loading partner profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!partnerData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
        <Header />
        <main className="pt-[148px] flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Building2" size={48} className="text-slate-300 mx-auto mb-16" />
            <h2 className="text-xl font-bold text-slate-700 mb-8">No Partner Profile Found</h2>
            <p className="text-slate-500 text-sm mb-24">Register a partner first to view their profile.</p>
            <button
              onClick={() => navigate('/partner-registration-dashboard')}
              className="px-24 py-12 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-smooth"
            >
              Register Partner
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Partner Profile Portal - PartnerHub</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
        <Header />

        <main className="pt-[148px]">
          <div className="max-w-[1600px] mx-auto px-24 py-32">

            {/* Partner Switcher Bar */}
            <div className="relative z-20 bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-md border border-slate-200/60 px-24 py-16 mb-24 flex items-center gap-16 flex-wrap">
              <div className="flex items-center gap-10 text-sm text-slate-500 font-medium">
                <Icon name="ArrowLeftRight" size={16} />
                <span>Viewing:</span>
              </div>
              <PartnerSwitcher
                currentPartnerId={currentPartnerId}
                onSelect={handlePartnerSwitch}
              />
              <div className="flex items-center gap-8 ml-auto text-sm text-slate-500">
                <Icon name="Info" size={14} />
                <span>Switch partners without leaving this page</span>
              </div>
            </div>

            {/* Partner Header Section */}
            <div className="relative z-10 bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-lg border border-slate-200/60 p-32 mb-24">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-24">
                <div className="flex items-center gap-24">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl blur-xl"></div>
                    <img
                      src={partnerData?.logo}
                      alt={`${partnerData?.companyName} company logo`}
                      className="relative w-24 h-24 rounded-2xl object-cover border-2 border-white shadow-lg"
                    />
                  </div>

                  <div>
                    <div className="flex items-center gap-12 mb-8">
                      <h1 className="text-3xl font-bold text-slate-900">{partnerData?.companyName}</h1>
                      <span className={`px-16 py-6 rounded-full text-sm font-semibold bg-gradient-to-r ${getTierColor(partnerData?.tier)} text-white shadow-md`}>
                        {partnerData?.tier} Partner
                      </span>
                    </div>
                    <div className="flex items-center gap-16 text-sm text-slate-600 flex-wrap">
                      <div className="flex items-center gap-6">
                        <Icon name="Calendar" size={16} className="text-slate-400" />
                        <span>Partnership: {partnerData?.partnershipDuration}</span>
                      </div>
                      <div className="h-4 w-px bg-slate-300"></div>
                      <div className="flex items-center gap-6">
                        <Icon name="DollarSign" size={16} className="text-slate-400" />
                        <span>Revenue: {partnerData?.revenueContribution}</span>
                      </div>
                      <div className="h-4 w-px bg-slate-300"></div>
                      <div className="flex items-center gap-6">
                        <div className={`w-2 h-2 rounded-full ${partnerData?.status === 'Active' ? 'bg-success' : 'bg-slate-400'}`}></div>
                        <span className="capitalize">{partnerData?.status?.replace(/_/g, ' ')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-12">
                  <div className="flex items-center gap-12">
                    <span className="text-sm font-medium text-slate-600">Health Score</span>
                    <div className="flex items-center gap-8">
                      <div className="relative w-16 h-16">
                        <svg className="w-full h-full transform -rotate-90">
                          <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="6" fill="none" className="text-slate-200" />
                          <circle
                            cx="32" cy="32" r="28"
                            stroke="currentColor" strokeWidth="6" fill="none"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - (partnerData?.healthScore || 0) / 100)}`}
                            className={getHealthScoreColor(partnerData?.healthScore)}
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${getHealthScoreColor(partnerData?.healthScore)}`}>
                          {partnerData?.healthScore}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-8">
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="px-20 py-10 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-semibold text-sm shadow-md hover:shadow-lg transition-smooth flex items-center gap-8"
                    >
                      <Icon name="Edit" size={16} />
                      Edit Profile
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        className="px-20 py-10 bg-white border border-slate-300 text-slate-700 rounded-lg font-semibold text-sm hover:bg-slate-50 transition-smooth flex items-center gap-8"
                      >
                        <Icon name="Download" size={16} />
                        Generate Summary
                        <Icon name="ChevronDown" size={14} />
                      </button>
                      {showExportMenu && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowExportMenu(false)} />
                          <div className="absolute right-0 top-full mt-8 w-48 bg-white rounded-xl shadow-xl border border-slate-200 z-40 overflow-hidden">
                            <button
                              onClick={() => handleGenerateSummary('csv')}
                              className="w-full flex items-center gap-12 px-16 py-12 hover:bg-slate-50 text-sm text-slate-700 transition-smooth"
                            >
                              <Icon name="FileSpreadsheet" size={16} className="text-green-600" />
                              Export as CSV
                            </button>
                            <button
                              onClick={() => handleGenerateSummary('pdf')}
                              className="w-full flex items-center gap-12 px-16 py-12 hover:bg-slate-50 text-sm text-slate-700 transition-smooth"
                            >
                              <Icon name="FileText" size={16} className="text-red-500" />
                              Export as PDF
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section Navigation */}
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-elevation-md border border-slate-200/60 p-8 mb-24">
              <div className="flex items-center gap-8 overflow-x-auto">
                {sections?.map((section) => (
                  <button
                    key={section?.id}
                    onClick={() => setActiveSection(section?.id)}
                    className={`
                      flex items-center gap-10 px-20 py-12 rounded-xl font-semibold text-sm whitespace-nowrap transition-smooth
                      ${activeSection === section?.id
                        ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary border border-primary/20' :'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                    `}
                  >
                    <Icon name={section?.icon} size={18} />
                    {section?.label}
                    {section?.id === 'notes' && (
                      <span className="px-6 py-2 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">Admin</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-24">
              <div className="lg:col-span-2 space-y-24">
                {activeSection === 'overview' && <CompanyOverview partner={rawPartner} />}
                {activeSection === 'products' && <ProductsServicesSection partnerId={partnerData?.id} />}
                {activeSection === 'documents' && <LegalDocumentsSection partnerId={partnerData?.id} />}
                {activeSection === 'contacts' && <ContactsDirectory partnerId={partnerData?.id} />}
                {activeSection === 'governance' && <GovernanceTab partnerId={partnerData?.id} partnerName={partnerData?.companyName} />}
                {activeSection === 'setup' && <SetupTab partner={partnerData} rawPartner={rawPartner} />}
                {activeSection === 'notes' && <NotesTab partnerId={partnerData?.id} currentUser={userProfile} />}
              </div>
              <div className="lg:col-span-1">
                <PartnerHealthDashboard healthScore={partnerData?.healthScore} partnerId={partnerData?.id} />
              </div>
            </div>
          </div>
        </main>
      </div>

      {showEditModal && partnerData && (
        <EditProfileModal
          partner={partnerData}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadPartner();
          }}
        />
      )}
    </>
  );
};

export default PartnerProfilePortal;